/** @file editMenu: Edit: Undo, Redo, Copy pose, Paste pose, Key pose, Key all (the timeline's own helpers). */
import { keyFrame } from '../panels/timeline/keyFrame';
import { pastePose } from '../panels/timeline/pastePose';
import { historyView } from '../state/historyView';
import type { MenuBuilder } from './menuTypes';

/**
 * Undo and Redo are the server's, named after the step they take ("Undo pose armR @3") and disabled when the revision
 * log has nothing that way; Copy/Paste pose keep the clip and frame in `shell.poseClipboard` and replay
 * `clip.copyPose` at the playhead; Key pose keys the selected nodes, Key all the whole pose, both at the playhead frame.
 */
export const editMenu: MenuBuilder = (ctx) => {
  const s = ctx.store.getState();
  const h = historyView(s);
  const clip = s.playhead.clip;
  const named = (verb: string, label: string | null): string => (label === null ? verb : `${verb} ${label}`);
  return [
    {
      kind: 'item', id: 'edit.undo', label: named('Undo', h.nextUndoLabel), chord: ctx.chordOf('history.undo'), disabled: !h.canUndo,
      onSelect: () => { void s.undo(); },
    },
    {
      kind: 'item', id: 'edit.redo', label: named('Redo', h.nextRedoLabel), chord: ctx.chordOf('history.redo', 'history.redoAlt'), disabled: !h.canRedo,
      onSelect: () => { void s.redo(); },
    },
    { kind: 'separator' },
    {
      kind: 'item', id: 'edit.copyPose', label: 'Copy pose', chord: ctx.chordOf('timeline.copyPose'), disabled: clip === null,
      onSelect: () => { if (clip !== null) s.setShell({ poseClipboard: { clip, frame: s.playhead.frame } }); },
    },
    {
      kind: 'item', id: 'edit.pastePose', label: 'Paste pose', chord: ctx.chordOf('timeline.pastePose'), disabled: clip === null || s.shell.poseClipboard === null,
      onSelect: () => { pastePose(ctx.store, { current: ctx.store.getState().shell.poseClipboard }, ctx.store.getState().playhead.frame); },
    },
    { kind: 'separator' },
    {
      kind: 'item', id: 'edit.keyPose', label: 'Key pose', chord: ctx.chordOf('viewport.key.joint', 'timeline.keyPose'), disabled: clip === null,
      hint: 'Keys the selected nodes at the playhead', onSelect: () => { keyFrame(ctx.store, s.playhead.frame, s.selection.nodes); },
    },
    {
      kind: 'item', id: 'edit.keyAll', label: 'Key all', chord: ctx.chordOf('viewport.key.all', 'timeline.keyAll'), disabled: clip === null,
      hint: 'Keys every node at the playhead', onSelect: () => { keyFrame(ctx.store, s.playhead.frame); },
    },
  ];
};
